import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { useAaveDataLayer } from '@/domain/market-info/aave-data-layer/useAaveDataLayer'
import { EPOCH_LENGTH } from '@/domain/market-info/consts'
import { updatePositionSummary } from '@/domain/market-info/updatePositionSummary'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { Token } from '@/domain/types/Token'
import { useMarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { Objective } from '@/features/actions/logic/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'
import { RepayDialogFormNormalizedData, useDebouncedRepayFormValues } from './form'
import { PageState, PageStatus } from '../../common/types'
import { getRepayOptions, getTokenDebt } from './assets'
import { getFormFieldsForRepayDialog, getRepayDialogFormValidator, RepayFormSchema, FormFieldsForDialog } from './form'
import { makeUpdatedPositionOverview } from './positionOverview'
import { PositionOverview } from './types'
import { createRepayObjectives } from './useCreateRepayObjectives'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { useLiquidationDetails } from '@/features/easy-borrow/logic/useLiquidationDetails'

export interface UseRepayDialogOptions {
  initialToken: Token
}

export interface UseRepayDialogResult {
  repayOptions: TokenWithBalance[]
  assetsToRepayFields: FormFieldsForDialog
  repaymentAsset: TokenWithValue
  objectives: Objective[]
  pageStatus: PageStatus
  form: UseFormReturn<RepayFormSchema>
  actionsInProgress: boolean
  setActionsInProgress: (actionsInProgress: boolean) => void
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
  currentLiquidationPrice?: NormalizedUnitNumber
  updatedLiquidationPrice?: NormalizedUnitNumber
}

export function useRepayDialog({ initialToken }: UseRepayDialogOptions): UseRepayDialogResult {
  const chainId = useChainId()
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })

  const { marketInfo: marketInfoIn1Epoch } = useMarketInfo({ timeAdvance: EPOCH_LENGTH, chainId })
  const { marketInfo: marketInfoIn2Epochs } = useMarketInfo({ timeAdvance: 2 * EPOCH_LENGTH, chainId })
  const walletInfo = useMarketWalletInfo({ chainId })

  const [rawActionsInProgress, setRawActionsInProgress] = useState(false)
  const actionsInProgress = rawActionsInProgress
  const setActionsInProgress = useCallback((isInProgress: boolean) => {
    setRawActionsInProgress(isInProgress)
  }, [])

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const form = useForm<RepayFormSchema>({
    resolver: zodResolver(getRepayDialogFormValidator(marketInfo, walletInfo), undefined, { raw: true }),
    defaultValues: {
      repayAsset: {
        symbol: initialToken.symbol,
        value: '',
        isMaxSelected: false,
      },
    },
    mode: 'onChange',
  })

  const repayOptions = getRepayOptions({
    token: initialToken,
    marketInfo,
    walletInfo,
    nativeAssetInfo: marketInfo.nativeAssetInfo,
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedRepayFormValues({
    form,
    marketInfo,
  })

  const frozenFormValues: RepayDialogFormNormalizedData = useConditionalFreeze(formValues, pageStatus === 'success')
  const repaymentAsset = frozenFormValues.repayAsset

  const isRepayMaxSelected = form.watch('repayAsset.isMaxSelected')

  useEffect(() => {
    if (isRepayMaxSelected) {
      form.trigger()
    }
  }, [isRepayMaxSelected, form.trigger])

  const assetsToRepayFields = getFormFieldsForRepayDialog({
    form,
    marketInfo,
    walletInfo,
  })

  const debt = getTokenDebt(marketInfo, {
    ...repaymentAsset,
    value: repaymentAsset.value,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const objectives = useMemo(() => {
    return createRepayObjectives({
      repaymentAsset,
      initialToken,
      marketInfoIn1Epoch,
      marketInfoIn2Epochs,
      walletInfo,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repaymentAsset.token.symbol, repaymentAsset.value, initialToken.symbol])

  const currentPositionOverview = {
    healthFactor: marketInfo.userPositionSummary.healthFactor,
    debt: repaymentAsset.position.borrowBalance,
    collateral: NormalizedUnitNumber(0),
  }

  const updatedUserSummary = updatePositionSummary({
    repays: [
      {
        ...repaymentAsset,
        value: repaymentAsset.value,
      },
    ],
    withdrawals: [],
    marketInfo,
    aaveData,
    nativeAssetInfo: marketInfo.nativeAssetInfo,
  })

  const updatedPositionOverview = repaymentAsset.value.eq(0)
    ? undefined
    : makeUpdatedPositionOverview({
        healthFactor: updatedUserSummary.healthFactor,
        debt,
        collateral: NormalizedUnitNumber(0),
      })

  const liquidationDetails = useLiquidationDetails({
    marketInfo,
    tokensToDeposit: [],
    tokensToBorrow: [
      {
        token: repaymentAsset.token,
        value: NormalizedUnitNumber(0),
      },
    ],
    liquidationThreshold: updatedUserSummary.currentLiquidationThreshold,
    freeze: false,
  })

  const updatedLiquidationDetails = useLiquidationDetails({
    marketInfo,
    tokensToDeposit: [],
    tokensToBorrow: [
      {
        token: repaymentAsset.token,
        value: repaymentAsset.value.eq(0)
          ? NormalizedUnitNumber(0)
          : NormalizedUnitNumber(repaymentAsset.value.multipliedBy(-1)),
      },
    ],
    liquidationThreshold: updatedUserSummary.currentLiquidationThreshold,
    freeze: false,
  })

  const currentLiquidationPrice = liquidationDetails?.liquidationPrice

  const updatedLiquidationPrice = updatedLiquidationDetails?.liquidationPrice?.lt(0)
    ? NormalizedUnitNumber(0)
    : updatedLiquidationDetails?.liquidationPrice || NormalizedUnitNumber(0)

  return {
    form,
    repayOptions,
    assetsToRepayFields,
    repaymentAsset,
    objectives,
    pageStatus: {
      state: pageStatus,
      actionsEnabled: repaymentAsset.value.gt(0) && isFormValid && !isDebouncing,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    currentPositionOverview,
    updatedPositionOverview,
    currentLiquidationPrice,
    updatedLiquidationPrice,
    actionsInProgress,
    setActionsInProgress,
  }
}
